import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'

import {
  clearRfqUpdates,
  listRfqUpdates,
  markAllRfqUpdatesRead,
  markRfqUpdateRead,
  type RfqUpdateDto,
} from '../api/rfqUpdates'
import { useAuth } from '../auth/AuthProvider'

type RfqNotificationsContextValue = {
  items: RfqUpdateDto[]
  unreadCount: number
  loading: boolean
  error: string | null
  refresh: () => Promise<void>
  markRead: (updateId: number) => Promise<void>
  markAllRead: () => Promise<void>
  clearAll: () => Promise<void>
}

const RfqNotificationsContext = createContext<RfqNotificationsContextValue | null>(null)

export const RfqNotificationsProvider = ({ children }: { children: ReactNode }) => {
  const { session, role } = useAuth()
  const [items, setItems] = useState<RfqUpdateDto[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const unreadCount = useMemo(() => items.filter((item) => !item.read).length, [items])

  const refresh = useCallback(async () => {
    if (!session || role !== 'buyer') {
      setItems([])
      setError(null)
      setLoading(false)
      return
    }
    setLoading(true)
    setError(null)
    try {
      const updatesPage = await listRfqUpdates(session.organizationId)
      setItems(Array.isArray(updatesPage.items) ? updatesPage.items : [])
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError.message : 'Не удалось загрузить уведомления')
    } finally {
      setLoading(false)
    }
  }, [role, session])

  useEffect(() => {
    void refresh()
  }, [refresh])

  const markRead = useCallback(
    async (updateId: number) => {
      if (!session) {
        return
      }
      setItems((current) =>
        current.map((item) => (item.id === updateId ? { ...item, read: true } : item)),
      )
      try {
        await markRfqUpdateRead(session.organizationId, updateId)
      } catch (markError) {
        await refresh()
        setError(markError instanceof Error ? markError.message : 'Не удалось отметить прочитанным')
      }
    },
    [refresh, session],
  )

  const markAllRead = useCallback(async () => {
    if (!session) {
      return
    }
    setItems((current) => current.map((item) => ({ ...item, read: true })))
    try {
      await markAllRfqUpdatesRead(session.organizationId)
    } catch (markError) {
      await refresh()
      setError(
        markError instanceof Error ? markError.message : 'Не удалось отметить все прочитанными',
      )
    }
  }, [refresh, session])

  const clearAll = useCallback(async () => {
    if (!session) {
      return
    }
    setItems([])
    try {
      await clearRfqUpdates(session.organizationId)
    } catch (clearError) {
      await refresh()
      setError(clearError instanceof Error ? clearError.message : 'Не удалось очистить уведомления')
    }
  }, [refresh, session])

  const value = useMemo(
    () => ({
      items,
      unreadCount,
      loading,
      error,
      refresh,
      markRead,
      markAllRead,
      clearAll,
    }),
    [clearAll, error, items, loading, markAllRead, markRead, refresh, unreadCount],
  )

  return (
    <RfqNotificationsContext.Provider value={value}>{children}</RfqNotificationsContext.Provider>
  )
}

export const useRfqNotifications = () => {
  const context = useContext(RfqNotificationsContext)
  if (!context) {
    throw new Error('useRfqNotifications must be used within RfqNotificationsProvider')
  }
  return context
}

export const useNotificationsUnreadCount = () => useRfqNotifications().unreadCount
